import React, {useEffect} from 'react'
import {configureGlobalAxiosSettings} from './util'
import {Redirect, Router} from '@reach/router'
import * as ReactGA from 'react-ga'
import {Helmet} from 'react-helmet'
import {
  AcceptInvitation,
  ArchiveEnvironment,
  Account,
  Accounts,
  Archives,
  Activities,
  ApiDocs,
  ArchiveService,
  Backups,
  Billing,
  Conflicts,
  ChangePassword,
  CloseAccount,
  Console,
  Contract,
  CopyTo,
  CreateAccount,
  Directory,
  DirectorySelection,
  Docs,
  Environments,
  ForgotPassword,
  Help,
  LeaveAccount,
  Login,
  Logs,
  ManageVersions,
  Notifications,
  NotFound,
  Profile,
  PublishedDocs,
  RemoveEnvironment,
  RemoveResource,
  RemoveResponse,
  RemoveService,
  RemoveUser,
  RemoveVersion,
  ResetPassword,
  Resources,
  Responses,
  RestoreBackup,
  RestoreArchive,
  Service,
  Services,
  SuperUserAccounts,
  UpdateProfile,
  UploadContract,
  UpsertEnvironment,
  UpsertResource,
  UpsertResponse,
  UpsertService,
  UpsertUser,
  UpsertVersion,
  Users,
  VersionDocs,
  ArchiveVersion,
  Versions
} from './component'

(function (history) {
  const pushState = history.pushState
  history.pushState = function (state) {
    if (typeof history.onpushstate == 'function') {
      history.onpushstate({state: state})
    }
    const args = Array.from(arguments)
    history.lastUrl = args[2]
    ReactGA.pageview(history.lastUrl)
    return pushState.apply(history, arguments)
  }
})(window.history)

configureGlobalAxiosSettings()

export default function App() {

  const SERVICES_PREFIX = `services`
  const VERSIONS_PREFIX = `${SERVICES_PREFIX}/:serviceId/versions`
  const ENVIRONMENTS_PREFIX = `${SERVICES_PREFIX}/:serviceId/versions/:versionId/environments`
  const RESOURCES_PREFIX = `${SERVICES_PREFIX}/:serviceId/versions/:versionId/environments/:environmentId/resources`
  const RESPONSES_PREFIX = `${SERVICES_PREFIX}/:serviceId/versions/:versionId/environments/:environmentId/resources/:resourceId/responses`
  const USERS_PREFIX = 'account/users'

  useEffect(() => {
    const isLocal = process.env.REACT_APP_ENV !== 'production'
    ReactGA.initialize('UA-157459236-1', {testMode: isLocal})
    ReactGA.pageview(window.location.pathname)
  }, [])

  return (
    <>
      <Helmet>
        <title>Service Mocks</title>
        <meta name="description" content="Collaborative API design and testing"/>
      </Helmet>
      <Router>
        <Console path='/c'>
          <Notifications path='notifications'/>
          <CreateAccount path='auth/create-account'/>
          <Login path='auth/login'/>
          <ForgotPassword path='auth/forgot-password'/>
          <ResetPassword path='auth/reset-password'/>
          <AcceptInvitation path='auth/accept-invitation'/>

          <UploadContract path={`${SERVICES_PREFIX}/upload-contract`}/>
          <UpsertService path={`${SERVICES_PREFIX}/add`}/>
          <UpsertService path={`${SERVICES_PREFIX}/:serviceId/edit`}/>
          <RemoveService path={`${SERVICES_PREFIX}/:serviceId/remove`}/>
          <ArchiveService path={`${SERVICES_PREFIX}/:serviceId/archive`}/>

          <RemoveVersion path={`${VERSIONS_PREFIX}/manage/:versionId/remove`}/>
          <UpsertVersion path={`${VERSIONS_PREFIX}/manage/:versionId/edit`}/>
          <UpsertVersion path={`${VERSIONS_PREFIX}/manage/add`}/>
          <ArchiveVersion path={`${VERSIONS_PREFIX}/manage/:versionId/archive`}/>

          <UpsertEnvironment path={`${ENVIRONMENTS_PREFIX}/:environmentId/edit`}/>
          <RemoveEnvironment path={`${ENVIRONMENTS_PREFIX}/:environmentId/remove`}/>
          <ArchiveEnvironment path={`${ENVIRONMENTS_PREFIX}/:environmentId/archive`}/>
          <UpsertEnvironment path={`${ENVIRONMENTS_PREFIX}/add`}/>

          <UpsertResource path={`${RESOURCES_PREFIX}/:resourceId/edit`}/>
          <RemoveResource path={`${RESOURCES_PREFIX}/:resourceId/remove`}/>
          <UpsertResource path={`${RESOURCES_PREFIX}/add`}/>
          <CopyTo path={`${RESOURCES_PREFIX}/:resourceId/copy-to/:copyToServiceId`}/>

          <UpsertResponse path={`${RESPONSES_PREFIX}/:responseId/edit`}/>
          <RemoveResponse path={`${RESPONSES_PREFIX}/:responseId/remove`}/>
          <UpsertResponse path={`${RESPONSES_PREFIX}/add`}/>
          <CopyTo path={`${RESPONSES_PREFIX}/:responseId/copy-to/:copyToServiceId`}/>

          <RemoveUser path={`${USERS_PREFIX}/:userId/remove`}/>
          <UpsertUser path={`${USERS_PREFIX}/invite`} invite/>


          <Services path='services'>
            <Service path=':serviceId'>
              <Versions path='versions'>
                <ManageVersions path='manage'/>
                <Environments path=':versionId/environments'>
                  <Activities path=':environmentId/activity' showParents={[]}/>
                  <Activities path=':environmentId/activity/:activityId' showParents={[]}/>
                  <Resources path=':environmentId/resources'>
                    <Responses path=':resourceId/responses' tab='responses'/>
                    <Logs path=':resourceId/logs' tab='logs'/>
                  </Resources>
                </Environments>
                <Contract path=':versionId/contract'/>
                <VersionDocs path=':versionId/docs'/>
                <Conflicts path=':versionId/conflicts'/>
              </Versions>
            </Service>
          </Services>
          <Account path='account'>
            <Profile default>
              <UpdateProfile default/>
              <Accounts path='accounts'>
                <LeaveAccount path=':accountId/leave'/>
              </Accounts>
              <ChangePassword path='change-password'/>
              <CloseAccount path='close-account'/>
            </Profile>
            <Users path='users'/>
            <Billing path='billing'/>
            <Activities path='activity'/>
            <Activities path='activity/:activityId'/>
            <RestoreBackup path='backups/:backupId/restore'/>
            <Backups path='backups'/>
            <RestoreArchive path='existing-archives/:archiveId/restore'/>
            <Archives path='suggested-archives' suggested/>
            <Archives path='existing-archives' suggested={false}/>
          </Account>
          <SuperUserAccounts path='su/*'/>
          <Docs path='docs/*'/>
          <Help path='help'/>
        </Console>
        <ApiDocs path='/c/api/docs'/>
        <Directory path='/c/directory'>
          <DirectorySelection path='services/:serviceId/versions/:versionId/display/:display'/>
        </Directory>
        <PublishedDocs path='/d/:environmentId'/>
        <PublishedDocs path='/d/:accountName/:serviceName/:versionName/:environmentName'/>
        <Redirect from="/" to="/c/services" noThrow/>
        <NotFound default/>
      </Router>
    </>
  )
}

